import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Input,
  Textarea,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Image,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  useDisclosure,
  useToast,
  Spinner,
  IconButton,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useColorModeValue,
  HStack,
  Divider,
  Alert,
  AlertIcon,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { EditIcon, DownloadIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  FiPlus,
  FiBarChart2,
  FiRefreshCw,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiPauseCircle,
  FiPlayCircle,
  FiActivity,
  FiChevronUp,
  FiChevronDown,
  FiEye,
  FiEyeOff,
  FiInfo,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import TicketFilters from "../components/TicketFilters.jsx";
import api from "../services/api";
import { employeeService } from "../modules/employee/employeeService";
import { useTicketConstants } from "../hooks/useTicketConstants";

const getDisplayName = (entity) => {
  if (!entity) return "-";
  if (entity.name) return entity.name;

  const fullName = [entity.firstName, entity.lastName]
    .filter(Boolean)
    .join(" ");
  return fullName || entity.email || entity.employeeId || "-";
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString();
};

// Calculate ticket age in days
const getTicketAge = (ticket) => {
  // Use backend-computed value if available
  if (ticket && typeof ticket.ageInDays === "number") {
    const days = ticket.ageInDays;
    if (days === 0) return "Today";
    if (days === 1) return "1 day";
    return `${days} days`;
  }

  // Fallback to client-side calculation
  const createdAt = ticket?.createdAt;
  if (!createdAt) return "-";
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return "-";

  const now = new Date();
  const ageInMs = now - created;
  const ageInDays = Math.floor(ageInMs / (1000 * 60 * 60 * 24));

  if (ageInDays === 0) return "Today";
  if (ageInDays === 1) return "1 day";
  return `${ageInDays} days`;
};

// Calculate days until SLA due date
const getDaysUntilDue = (ticket) => {
  // Use backend-computed values if available
  if (ticket && ticket.slaStatus) {
    if (ticket.slaStatus === "none") {
      return { text: "-", status: "none" };
    }

    const days = ticket.daysUntilDue;
    if (days < 0) {
      return { text: `${Math.abs(days)}d overdue`, status: ticket.slaStatus };
    } else if (days === 0) {
      return { text: "Due today", status: ticket.slaStatus };
    } else if (days === 1) {
      return { text: "1 day left", status: ticket.slaStatus };
    }
    return { text: `${days} days`, status: ticket.slaStatus };
  }

  // Fallback to client-side calculation (supports both dueDate and legacy slaDueDate)
  const ticketDueDate = getTicketDueDate(ticket);
  if (!ticketDueDate) return { text: "-", status: "none" };

  const dueDate = new Date(ticketDueDate);
  if (Number.isNaN(dueDate.getTime())) return { text: "-", status: "none" };

  const now = new Date();
  const diffInMs = dueDate - now;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) {
    return { text: `${Math.abs(diffInDays)}d overdue`, status: "breached" };
  } else if (diffInDays === 0) {
    return { text: "Due today", status: "at-risk" };
  } else if (diffInDays === 1) {
    return { text: "1 day left", status: "at-risk" };
  } else if (diffInDays <= 3) {
    return { text: `${diffInDays} days left`, status: "at-risk" };
  }
  return { text: `${diffInDays} days`, status: "on-track" };
};

// Get SLA status badge color
const getSlaStatusColor = (status) => {
  switch (status) {
    case "breached":
      return "red";
    case "at-risk":
      return "yellow";
    case "on-track":
      return "green";
    default:
      return "gray";
  }
};

const normalizeTicketListResponse = (payload) => {
  if (!payload) {
    return { rows: [], totalCount: 0, totalPages: 1 };
  }

  const directRows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.data?.tickets)
      ? payload.data.tickets
      : Array.isArray(payload?.tickets)
        ? payload.tickets
        : [];

  const totalCount =
    payload?.totalCount ?? payload?.data?.totalCount ?? directRows.length ?? 0;
  const totalPages = payload?.totalPages ?? payload?.data?.totalPages ?? 1;

  return {
    rows: directRows,
    totalCount: Number(totalCount) || directRows.length,
    totalPages: Number(totalPages) || 1,
  };
};

import { useAuth } from "../context/AuthContext";

export default function Ticketing() {
  const { user } = useAuth();

  // Fetch ticket constants from server
  const {
    STATUS_TABS,
    WORKFLOW_STATE,
    getWorkflowStateColor,
    getWorkflowStateLabel,
    getTicketWorkflowState,
    getTicketDueDate,
    getPriorityColor,
    loading: constantsLoading,
  } = useTicketConstants();

  const [allTickets, setAllTickets] = useState([]);
  const [totalTicketCount, setTotalTicketCount] = useState(0);
  const [totalTicketPages, setTotalTicketPages] = useState(1);
  const [summaryReport, setSummaryReport] = useState(null);
  const [overdueReport, setOverdueReport] = useState({ rows: [] });
  const [slaBreachedRows, setSlaBreachedRows] = useState([]);
  const [slaAtRiskRows, setSlaAtRiskRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [requestedByFilter, setRequestedByFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [sprintFilter, setSprintFilter] = useState("");
  const [milestoneFilter, setMilestoneFilter] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketViewMode, setTicketViewMode] = useState("personal"); // "personal" or "all"
  const [allowedStatuses, setAllowedStatuses] = useState([]);
  const [workflowMetaLoading, setWorkflowMetaLoading] = useState(false);
  const [deletingTicketId, setDeletingTicketId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [ticketSla, setTicketSla] = useState(null);
  const [ticketSlaLoading, setTicketSlaLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [newCommentType, setNewCommentType] = useState("public");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [slaStatusFilter, setSlaStatusFilter] = useState("");
  const [ageRangeFilter, setAgeRangeFilter] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  const [transitionComment, setTransitionComment] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [slaActionLoading, setSlaActionLoading] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isInfoOpen,
    onOpen: onInfoOpen,
    onClose: onInfoClose,
  } = useDisclosure();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [allEmployees, setAllEmployees] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showKpis, setShowKpis] = useState(() => {
    try {
      const v = localStorage.getItem("ticketing.showKpis");
      if (v === null) return true;
      return v === "true" || v === "1";
    } catch (e) {
      return true;
    }
  });
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    workflowState: "",
    priority: "",
    assignedTo: "",
    category: "",
    dueDate: "",
    attachmentsText: "",
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [fileInputRef] = useState(React.createRef());

  useEffect(() => {
    try {
      localStorage.setItem("ticketing.showKpis", showKpis ? "1" : "0");
    } catch (e) {
      // ignore storage errors
    }
  }, [showKpis]);

  // Initialize allowedStatuses when STATUS_TABS loads from server
  useEffect(() => {
    if (STATUS_TABS && STATUS_TABS.length > 0) {
      setAllowedStatuses(
        STATUS_TABS.filter((tab) => tab.value).map((tab) => tab.value),
      );
    }
  }, [STATUS_TABS]);

  useEffect(() => {
    const workflowState = searchParams.get("workflowState");
    const priority = searchParams.get("priority");
    const severity = searchParams.get("severity");

    if (workflowState) {
      setStatusFilter(workflowState);
      const matchedTabIndex = STATUS_TABS.findIndex(
        (tab) => tab.value === workflowState,
      );
      if (matchedTabIndex >= 0) {
        setActiveTabIndex(matchedTabIndex);
      }
    }

    if (priority) {
      setPriorityFilter(priority);
    }

    if (severity) {
      setSeverityFilter(severity);
    }
  }, [searchParams, STATUS_TABS]);

  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.700");
  const tableText = useColorModeValue("gray.800", "gray.200");
  const subText = useColorModeValue("gray.600", "gray.400");
  const noTicketBg = useColorModeValue("gray.50", "gray.700");
  const kpiText = useColorModeValue("gray.600", "gray.300");
  const kpiValue = useColorModeValue("gray.800", "white");
  const chartTrailColor = useColorModeValue("#EDF2F7", "#2D3748");
  const pageBg = useColorModeValue(
    "linear-gradient(130deg, #f8fbff 0%, #eef8f4 55%, #fffaf0 100%)",
    "gray.900",
  );

  useEffect(() => {
    if (!selectedTicket) {
      return;
    }

    const ticketDueDate = getTicketDueDate(selectedTicket);
    setEditForm({
      title: selectedTicket.title || "",
      description: selectedTicket.description || "",
      workflowState: getTicketWorkflowState(selectedTicket),
      priority: selectedTicket.priority || "Medium",
      assignedTo: selectedTicket.assignedTo?._id || "",
      category: selectedTicket.category || "",
      dueDate: ticketDueDate
        ? new Date(ticketDueDate).toISOString().slice(0, 16)
        : "",
      attachmentsText: (selectedTicket.attachments || []).join("\n"),
    });
  }, [selectedTicket]);

  useEffect(() => {
    loadTicketingData(true);
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    loadTicketList();
  }, [
    currentPage,
    itemsPerPage,
    activeTabIndex,
    statusFilter,
    priorityFilter,
    severityFilter,
    assignedToFilter,
    requestedByFilter,
    categoryFilter,
    slaStatusFilter,
    ageRangeFilter,
    sortBy,
    sortOrder,
  ]);

  // Handle filters from URL params after initial load completes
  useEffect(() => {
    if (loading || constantsLoading) return;

    const hasUrlParams =
      searchParams.has("workflowState") ||
      searchParams.has("priority") ||
      searchParams.has("severity");

    if (hasUrlParams) {
      // Reset to page 1 and reload with new filters
      setCurrentPage(1);
    }
  }, [loading, constantsLoading, searchParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    statusFilter,
    priorityFilter,
    severityFilter,
    assignedToFilter,
    requestedByFilter,
    itemsPerPage,
    activeTabIndex,
    categoryFilter,
    slaStatusFilter,
    ageRangeFilter,
  ]);

  // Trigger API call when ticketViewMode changes
  useEffect(() => {
    loadTicketList();
  }, [ticketViewMode]);

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new column with default descending order
      setSortBy(column);
      setSortOrder("desc");
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <FiChevronUp style={{ display: "inline", marginLeft: "4px" }} />
    ) : (
      <FiChevronDown style={{ display: "inline", marginLeft: "4px" }} />
    );
  };

  const loadTicketList = async () => {
    setRefreshing(true);

    try {
      const effectiveWorkflowState =
        statusFilter || STATUS_TABS[activeTabIndex]?.value || "";

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (effectiveWorkflowState) params.workflowState = effectiveWorkflowState;
      if (priorityFilter) params.priority = priorityFilter;
      if (severityFilter) params.severity = severityFilter;
      if (assignedToFilter) params.assignedTo = assignedToFilter;
      if (requestedByFilter) params.requestedBy = requestedByFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (slaStatusFilter) params.slaStatus = slaStatusFilter;
      if (ageRangeFilter) params.ageRange = ageRangeFilter;

      // Always send _viewMode for server-side filtering
      params._viewMode = ticketViewMode === "personal" ? "my" : "all";

      const ticketRes = await api.get("/api/tickets", { params });
      const normalized = normalizeTicketListResponse(ticketRes.data);

      setAllTickets(normalized.rows);
      setTotalTicketCount(normalized.totalCount);
      setTotalTicketPages(normalized.totalPages);
    } catch (error) {
      console.error("Error loading tickets:", error);
      toast({
        title: "Failed to load tickets",
        description:
          error.response?.data?.message || "Please try again in a moment.",
        status: "error",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const loadTicketContextData = async () => {
    const [
      employeesResult,
      clientResResult,
      reportResResult,
      overdueResResult,
      breachedResResult,
      atRiskResResult,
    ] = await Promise.allSettled([
      employeeService.getAllEmployees(),
      api.get("/clients/getAllClients"),
      api.get("/api/tickets/reports/summary", {
        params: {
          trendDays: 30,
          workloadLimit: 5,
        },
      }),
      api.get("/api/tickets/reports/overdue", {
        params: {
          limit: 5,
        },
      }),
      api.get("/api/tickets/sla/breached"),
      api.get("/api/tickets/sla/at-risk"),
    ]);

    const employees =
      employeesResult.status === "fulfilled" ? employeesResult.value : [];
    const clientRes =
      clientResResult.status === "fulfilled" ? clientResResult.value : null;
    const reportRes =
      reportResResult.status === "fulfilled" ? reportResResult.value : null;
    const overdueRes =
      overdueResResult.status === "fulfilled" ? overdueResResult.value : null;
    const breachedRes =
      breachedResResult.status === "fulfilled" ? breachedResResult.value : null;
    const atRiskRes =
      atRiskResResult.status === "fulfilled" ? atRiskResResult.value : null;

    setAllEmployees(Array.isArray(employees) ? employees : []);
    setAllClients(Array.isArray(clientRes?.data) ? clientRes.data : []);
    setSummaryReport(reportRes?.data?.data || null);
    setOverdueReport(overdueRes?.data?.data || { rows: [] });
    setSlaBreachedRows(breachedRes?.data?.data?.tickets || []);
    setSlaAtRiskRows(atRiskRes?.data?.data?.tickets || []);
  };

  const loadTicketingData = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      await Promise.all([loadTicketList(), loadTicketContextData()]);
    } catch (error) {
      console.error("Error loading ticketing data:", error);
      toast({
        title: "Failed to load ticketing data",
        description:
          error.response?.data?.message || "Please try again in a moment.",
        status: "error",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExportReport = async () => {
    try {
      // Export the currently visible tickets with new metrics
      const ticketsToExport = getStatusScopedTickets();

      if (ticketsToExport.length === 0) {
        toast({
          title: "No tickets to export",
          description: "There are no tickets matching your current filters.",
          status: "warning",
        });
        return;
      }

      const formatDate = (date) => {
        if (!date) return "";
        return new Date(date).toISOString().split("T")[0];
      };

      // Build Excel rows
      const rows = ticketsToExport.map((ticket) => {
        const ticketDue = getTicketDueDate(ticket);
        return {
          "Ticket Code": ticket.ticketCode || "",
          Title: ticket.title || "",
          Description: (ticket.description || "").replace(/[\r\n]+/g, " "),
          Status: getWorkflowStateLabel(getTicketWorkflowState(ticket)),
          Priority: ticket.priority || "",
          Category: ticket.category || "",
          "Age (Days)": ticket.ageInDays ?? getTicketAge(ticket),
          "SLA Due Date": formatDate(ticketDue),
          "Days Until Due":
            ticket.daysUntilDue ??
            (ticketDue
              ? Math.ceil(
                  (new Date(ticketDue) - new Date()) / (1000 * 60 * 60 * 24),
                )
              : ""),
          "SLA Status": ticket.slaStatus || "",
          Client: ticket.client?.name || "",
          "Requested By": getDisplayName(ticket.requestedBy) || "",
          "Assigned To": getDisplayName(ticket.assignedTo) || "",
          "Created At": formatDate(ticket.createdAt),
          "Updated At": formatDate(ticket.updatedAt),
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tickets");
      const filename = `tickets-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast({
        title: "Export successful",
        description: `Exported ${ticketsToExport.length} tickets to ${filename}`,
        status: "success",
      });
    } catch (error) {
      console.error("Report export failed:", error);
      toast({
        title: "Failed to export report",
        description: error.message || "Unable to generate Excel report.",
        status: "error",
      });
    }
  };

  const filteredTickets = allTickets.filter((ticket) => {
    const ticketCode = ticket.ticketCode || "";
    const title = ticket.title || "";

    const matchesSearch =
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticketCode.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesClient = clientFilter
      ? ticket.client?._id === clientFilter
      : true;

    const matchesProject = projectFilter
      ? ticket.projectId?._id === projectFilter
      : true;

    const matchesSprint = sprintFilter
      ? ticket.sprintId?._id === sprintFilter
      : true;

    const matchesMilestone = milestoneFilter
      ? ticket.milestoneId?._id === milestoneFilter
      : true;

    const matchesSeverity = severityFilter
      ? ticket.severity === severityFilter
      : true;

    return (
      matchesSearch &&
      matchesClient &&
      matchesProject &&
      matchesSprint &&
      matchesMilestone &&
      matchesSeverity
    );
  });

  const activeTabStatus = STATUS_TABS[activeTabIndex]?.value || "";

  const getStatusScopedTickets = () => filteredTickets;

  const overview = summaryReport?.overview || {};
  const totalTickets = overview.totalTickets || allTickets.length;
  const openTickets = overview.openTickets || 0;
  const closedTickets = overview.closedTickets || 0;
  const inProgressTickets = allTickets.filter(
    (ticket) => getTicketWorkflowState(ticket) === WORKFLOW_STATE.IN_PROGRESS,
  ).length;
  const overdueTicketsCount = overview.overdueTickets || 0;

  const getPercent = (value) => {
    if (!totalTickets) {
      return 0;
    }

    return Math.round((value / totalTickets) * 100);
  };

  // Additional KPI metrics
  const breachedSlaCount = slaBreachedRows.length;
  const atRiskCount = slaAtRiskRows.length;

  // Oldest open ticket age in days
  let oldestOpenTicketAge = 0;
  const openTicketsList = allTickets.filter((t) => {
    const state = getTicketWorkflowState(t);
    return state !== WORKFLOW_STATE.RESOLVED && state !== WORKFLOW_STATE.CLOSED;
  });
  if (openTicketsList.length > 0) {
    const oldestTicket = openTicketsList.reduce((oldest, current) => {
      const oldestDate = new Date(oldest.createdAt || Date.now());
      const currentDate = new Date(current.createdAt || Date.now());
      return currentDate < oldestDate ? current : oldest;
    });
    const now = new Date();
    const createdDate = new Date(oldestTicket.createdAt || now);
    oldestOpenTicketAge = Math.floor(
      (now - createdDate) / (1000 * 60 * 60 * 24),
    );
  }

  // Average resolution time in days (for closed tickets)
  let avgResolutionTime = 0;
  const closedTicketsList = allTickets.filter(
    (t) => t.status === "Resolved" || t.status === "Closed",
  );
  if (closedTicketsList.length > 0) {
    let totalResolutionTime = 0;
    closedTicketsList.forEach((ticket) => {
      if (ticket.createdAt && ticket.updatedAt) {
        const created = new Date(ticket.createdAt);
        const resolved = new Date(ticket.updatedAt);
        const diffMs = resolved - created;
        totalResolutionTime += diffMs;
      }
    });
    avgResolutionTime = Math.floor(
      totalResolutionTime / closedTicketsList.length / (1000 * 60 * 60 * 24),
    );
  }

  const handleEdit = (ticket) => {
    const loadTicketInsights = async () => {
      setCommentsLoading(true);
      setHistoryLoading(true);
      setTicketSlaLoading(true);

      try {
        const [commentsRes, historyRes, slaRes] = await Promise.all([
          api.get(`/api/tickets/${ticket._id}/comments`, {
            params: { page: 1, limit: 20, sortOrder: -1 },
          }),
          api.get(`/api/tickets/${ticket._id}/history`, {
            params: { page: 1, limit: 20 },
          }),
          api.get(`/api/tickets/${ticket._id}/sla`).catch(() => null),
        ]);

        setComments(commentsRes.data?.data || []);
        setHistoryRows(historyRes.data?.data || []);
        setTicketSla(slaRes?.data?.data || null);
      } catch (error) {
        console.error("Failed to load ticket insights:", error);
      } finally {
        setCommentsLoading(false);
        setHistoryLoading(false);
        setTicketSlaLoading(false);
      }
    };

    setWorkflowMetaLoading(true);
    const loadWorkflowMeta = async () => {
      try {
        const response = await api.get(
          `/api/tickets/${ticket._id}/workflow/metadata`,
        );

        const transitions = response.data?.data?.availableTransitions || [];
        const nextStates = transitions
          .filter((item) => item?.toState)
          .map((item) => item.toState);

        const currentState = getTicketWorkflowState(ticket);
        setAllowedStatuses(
          Array.from(new Set([currentState, ...nextStates].filter(Boolean))),
        );
      } catch (error) {
        setAllowedStatuses(
          STATUS_TABS.filter((tab) => tab.value).map((tab) => tab.value),
        );
      } finally {
        setWorkflowMetaLoading(false);
      }
    };

    loadWorkflowMeta();
    loadTicketInsights();
    setSelectedTicket(ticket);
    setNewComment("");
    setNewCommentType("public");
    setTransitionComment("");
    onOpen();
  };

  const handleSave = async () => {
    if (!selectedTicket) {
      return;
    }

    try {
      setSaveLoading(true);
      const currentState = getTicketWorkflowState(selectedTicket);
      const stateChanged = editForm.workflowState !== currentState;

      if (stateChanged) {
        await api.post(
          `/api/tickets/${selectedTicket._id}/workflow/transition`,
          {
            toState: editForm.workflowState,
            comment: transitionComment.trim() || undefined,
          },
        );
      }

      const payload = {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        category: editForm.category,
        assignedTo: editForm.assignedTo || null,
        dueDate: editForm.dueDate
          ? new Date(editForm.dueDate).toISOString()
          : null,
      };

      const response = await api.patch(
        `/api/tickets/${selectedTicket._id}`,
        payload,
      );

      if (response.data?.success) {
        toast({
          title: stateChanged
            ? "Ticket updated and workflow transitioned"
            : "Ticket updated successfully",
          status: "success",
        });
        onClose();
        setSelectedTicket(null);
        setTicketSla(null);
        setComments([]);
        setHistoryRows([]);
        loadTicketingData();
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast({
        title: "Failed to update ticket",
        description:
          error.response?.data?.message || "Please review the ticket data.",
        status: "error",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTicket) {
      return;
    }

    try {
      setUploadingAttachment(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/api/tickets/${selectedTicket._id}/attachments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        toast({
          title: "Attachment uploaded successfully",
          status: "success",
          duration: 2000,
        });

        // Refresh ticket data to show new attachment
        const refreshedTicket = await api.get(
          `/api/tickets/${selectedTicket._id}`,
        );
        if (refreshedTicket.data?.success) {
          const refreshedData = refreshedTicket.data.data;
          setSelectedTicket(refreshedData);
          setEditForm({
            title: refreshedData.title || "",
            description: refreshedData.description || "",
            workflowState: getTicketWorkflowState(refreshedData),
            priority: refreshedData.priority || "",
            assignedTo:
              typeof refreshedData.assignedTo === "object"
                ? refreshedData.assignedTo?._id || ""
                : refreshedData.assignedTo || "",
            category: refreshedData.category || "",
            dueDate: getTicketDueDate(refreshedData)
              ? new Date(getTicketDueDate(refreshedData))
                  .toISOString()
                  .slice(0, 16)
              : "",
            attachmentsText: (refreshedData.attachments || []).join("\n"),
          });
        }

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("File upload failed:", error);
      toast({
        title: "Failed to upload attachment",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentUrl) => {
    if (!selectedTicket) {
      return;
    }

    const shouldDelete = window.confirm(
      "Delete this attachment? This action cannot be undone.",
    );

    if (!shouldDelete) {
      return;
    }

    try {
      const response = await api.delete(
        `/api/tickets/${selectedTicket._id}/attachments`,
        {
          data: { attachmentUrl },
        },
      );

      if (response.data?.success) {
        toast({
          title: "Attachment deleted successfully",
          status: "success",
          duration: 2000,
        });

        // Refresh ticket data
        const refreshedTicket = await api.get(
          `/api/tickets/${selectedTicket._id}`,
        );
        if (refreshedTicket.data?.success) {
          const refreshedData = refreshedTicket.data.data;
          setSelectedTicket(refreshedData);
          setEditForm({
            title: refreshedData.title || "",
            description: refreshedData.description || "",
            workflowState: getTicketWorkflowState(refreshedData),
            priority: refreshedData.priority || "",
            assignedTo:
              typeof refreshedData.assignedTo === "object"
                ? refreshedData.assignedTo?._id || ""
                : refreshedData.assignedTo || "",
            category: refreshedData.category || "",
            dueDate: getTicketDueDate(refreshedData)
              ? new Date(getTicketDueDate(refreshedData))
                  .toISOString()
                  .slice(0, 16)
              : "",
            attachmentsText: (refreshedData.attachments || []).join("\n"),
          });
        }
      }
    } catch (error) {
      console.error("Delete attachment failed:", error);
      toast({
        title: "Failed to delete attachment",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDelete = async (ticketId) => {
    const shouldDelete = window.confirm(
      "Delete this ticket? This action cannot be undone.",
    );

    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingTicketId(ticketId);
      const response = await api.delete(`/api/tickets/${ticketId}`);

      if (response.data?.success) {
        toast({
          title: "Ticket deleted successfully",
          status: "success",
        });

        if (selectedTicket?._id === ticketId) {
          onClose();
          setSelectedTicket(null);
        }

        loadTicketingData();
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast({
        title: "Failed to delete ticket",
        description:
          error.response?.data?.message || "Please try again in a moment.",
        status: "error",
      });
    } finally {
      setDeletingTicketId(null);
    }
  };

  const handleCreateComment = async () => {
    if (!selectedTicket || !newComment.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      const response = await api.post(
        `/api/tickets/${selectedTicket._id}/comments`,
        {
          content: newComment.trim(),
          commentType: newCommentType,
        },
      );

      if (response.data?.success) {
        const commentsRes = await api.get(
          `/api/tickets/${selectedTicket._id}/comments`,
          {
            params: { page: 1, limit: 20, sortOrder: -1 },
          },
        );
        setComments(commentsRes.data?.data || []);
        setNewComment("");
        toast({ title: "Comment added", status: "success" });
      }
    } catch (error) {
      toast({
        title: "Failed to add comment",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = (comment) => {
    setEditingCommentId(comment._id);
    setEditCommentContent(comment.content || "");
  };

  const handleSaveEditComment = async () => {
    if (!editingCommentId || !selectedTicket) {
      return;
    }

    try {
      await api.put(`/api/tickets/comments/${editingCommentId}`, {
        content: editCommentContent,
      });

      const commentsRes = await api.get(
        `/api/tickets/${selectedTicket._id}/comments`,
        { params: { page: 1, limit: 20, sortOrder: -1 } },
      );
      setComments(commentsRes.data?.data || []);
      setEditingCommentId(null);
      setEditCommentContent("");
      toast({ title: "Comment updated", status: "success" });
    } catch (error) {
      toast({
        title: "Failed to update comment",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
      });
    }
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentContent("");
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) {
      return;
    }

    try {
      await api.delete(`/api/tickets/comments/${commentId}`);
      const commentsRes = await api.get(
        `/api/tickets/${selectedTicket._id}/comments`,
        {
          params: { page: 1, limit: 20, sortOrder: -1 },
        },
      );
      setComments(commentsRes.data?.data || []);
      toast({ title: "Comment deleted", status: "success" });
    } catch (error) {
      toast({
        title: "Failed to delete comment",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
      });
    }
  };

  const refreshTicketSla = async () => {
    if (!selectedTicket?._id) {
      return;
    }

    setTicketSlaLoading(true);
    try {
      const slaRes = await api.get(`/api/tickets/${selectedTicket._id}/sla`);
      setTicketSla(slaRes.data?.data || null);
    } catch (error) {
      setTicketSla(null);
    } finally {
      setTicketSlaLoading(false);
    }
  };

  const handleSlaAction = async (action) => {
    if (!selectedTicket?._id) {
      return;
    }

    const endpointByAction = {
      pause: "pause",
      resume: "resume",
      calculate: "calculate",
    };

    try {
      setSlaActionLoading(action);
      await api.post(
        `/api/tickets/${selectedTicket._id}/sla/${endpointByAction[action]}`,
      );
      await Promise.all([
        refreshTicketSla(),
        loadTicketContextData(),
        loadTicketList(),
      ]);
      toast({ title: `SLA ${action} completed`, status: "success" });
    } catch (error) {
      toast({
        title: `Unable to ${action} SLA`,
        description: error.response?.data?.message || "Please try again.",
        status: "error",
      });
    } finally {
      setSlaActionLoading("");
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <>
      <Box
        w="100%"
        p={4}
        bg={pageBg}
        borderRadius="2xl"
        border="1px solid"
        borderColor={cardBorder}
      >
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          gap={3}
          flexWrap="wrap"
        >
          <Box>
            <HStack align="center" spacing={2}>
              <Text
                fontSize="2xl"
                fontWeight="extrabold"
                color={tableText}
                letterSpacing="0.2px"
              >
                Ticketing Control Center
              </Text>
              <IconButton
                aria-label="Ticketing user manual"
                icon={<FiInfo />}
                size="sm"
                variant="ghost"
                onClick={onInfoOpen}
                title="Open Ticketing user manual"
              />
            </HStack>
            <Text fontSize="sm" color={subText}>
              Workflow-aware operations, SLA monitoring, and audited
              collaboration.
            </Text>
          </Box>
          <HStack spacing={3}>
            <IconButton
              icon={showKpis ? <FiEyeOff /> : <FiEye />}
              onClick={() => setShowKpis(!showKpis)}
              aria-label={showKpis ? "Hide KPIs" : "Show KPIs"}
              colorScheme={showKpis ? "gray" : "blue"}
              variant="outline"
              title={showKpis ? "Hide KPIs" : "Show KPIs"}
            />
            <IconButton
              icon={refreshing ? <Spinner size="sm" /> : <FiRefreshCw />}
              onClick={() => loadTicketingData()}
              aria-label="Refresh tickets"
              isDisabled={refreshing}
            />
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="teal"
              onClick={handleExportReport}
            >
              Export Report
            </Button>
          </HStack>
        </Flex>

        {showKpis && (
          <>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} spacing={6} mb={6}>
              {[
                {
                  label: "Total Tickets",
                  value: totalTickets,
                  percent: totalTickets ? 100 : 0,
                  icon: FiBarChart2,
                  color: "#4C51BF",
                },
                {
                  label: "Active Tickets",
                  value: openTickets,
                  percent: getPercent(openTickets),
                  icon: FiCheckCircle,
                  color: "#3182CE",
                },
                {
                  label: "In Progress",
                  value: inProgressTickets,
                  percent: getPercent(inProgressTickets),
                  icon: FiRefreshCw,
                  color: "#DD6B20",
                },
                {
                  label: "Closed Tickets",
                  value: closedTickets,
                  percent: getPercent(closedTickets),
                  icon: FiX,
                  color: "#718096",
                },
                {
                  label: "Overdue",
                  value: overdueTicketsCount,
                  percent: getPercent(overdueTicketsCount),
                  icon: FiAlertCircle,
                  color: "#E53E3E",
                },
              ].map((kpi) => (
                <Box
                  key={kpi.label}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  p={5}
                  borderRadius="xl"
                  boxShadow="sm"
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-3px)", boxShadow: "xl" }}
                >
                  <Box w="80px" mx="auto" mb={4}>
                    <CircularProgressbarWithChildren
                      value={kpi.percent}
                      styles={buildStyles({
                        pathColor: kpi.color,
                        trailColor: chartTrailColor,
                        strokeLinecap: "round",
                      })}
                    >
                      <Text fontSize="lg" fontWeight="bold" color={kpiText}>
                        {kpi.percent}%
                      </Text>
                    </CircularProgressbarWithChildren>
                  </Box>
                  <Flex align="center" justify="center" gap={2}>
                    <kpi.icon color={kpi.color} />
                    <Text fontSize="md" fontWeight="medium" color={kpiText}>
                      {kpi.label}
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="bold" color={kpiValue} mt={1}>
                    {kpi.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Additional Business Metrics KPI Cards */}
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6} mb={6}>
              {[
                {
                  label: "Breached SLA",
                  value: breachedSlaCount,
                  percent: getPercent(breachedSlaCount),
                  icon: FiAlertCircle,
                  color: "#E53E3E",
                },
                {
                  label: "At Risk",
                  value: atRiskCount,
                  percent: getPercent(atRiskCount),
                  icon: FiPauseCircle,
                  color: "#DD6B20",
                },
                {
                  label: "Avg Resolution",
                  value:
                    avgResolutionTime > 0 ? `${avgResolutionTime}d` : "N/A",
                  percent:
                    avgResolutionTime > 0
                      ? Math.min(100, (avgResolutionTime / 30) * 100)
                      : 0,
                  icon: FiActivity,
                  color: "#38A169",
                },
                {
                  label: "Oldest Open",
                  value:
                    oldestOpenTicketAge > 0 ? `${oldestOpenTicketAge}d` : "N/A",
                  percent:
                    oldestOpenTicketAge > 0
                      ? Math.min(100, (oldestOpenTicketAge / 30) * 100)
                      : 0,
                  icon: FiPlayCircle,
                  color: "#805AD5",
                },
              ].map((kpi) => (
                <Box
                  key={kpi.label}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  p={5}
                  borderRadius="xl"
                  boxShadow="sm"
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-3px)", boxShadow: "xl" }}
                >
                  <Box w="80px" mx="auto" mb={4}>
                    <CircularProgressbarWithChildren
                      value={kpi.percent}
                      styles={buildStyles({
                        pathColor: kpi.color,
                        trailColor: chartTrailColor,
                        strokeLinecap: "round",
                      })}
                    >
                      <Text fontSize="lg" fontWeight="bold" color={kpiText}>
                        {typeof kpi.percent === "number"
                          ? `${Math.round(kpi.percent)}%`
                          : "0%"}
                      </Text>
                    </CircularProgressbarWithChildren>
                  </Box>
                  <Flex align="center" justify="center" gap={2}>
                    <kpi.icon color={kpi.color} />
                    <Text fontSize="md" fontWeight="medium" color={kpiText}>
                      {kpi.label}
                    </Text>
                  </Flex>
                  <Text fontSize="xl" fontWeight="bold" color={kpiValue} mt={1}>
                    {kpi.value}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="xl"
                p={5}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="bold" color={tableText}>
                    Overdue Tickets Report
                  </Text>
                  <Badge colorScheme="red">
                    {overdueReport?.totalRows || 0}
                  </Badge>
                </Flex>
                <VStack align="stretch" spacing={3} divider={<Divider />}>
                  {(overdueReport?.rows || []).length > 0 ? (
                    overdueReport.rows.map((row) => (
                      <Box key={`${row.ticketId}-${row.title}`}>
                        <Text fontWeight="semibold" color={tableText}>
                          {row.ticketId} · {row.title}
                        </Text>
                        <Text fontSize="sm" color={subText}>
                          {row.assignedToName || "Unassigned"} ·{" "}
                          {row.priority || "-"} · {row.daysOverdue || 0} day(s)
                          overdue
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text color={subText}>No overdue tickets right now.</Text>
                  )}
                </VStack>
              </Box>

              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="xl"
                p={5}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="bold" color={tableText}>
                    Assignee Workload
                  </Text>
                  <Badge colorScheme="blue">
                    {summaryReport?.workload?.length || 0}
                  </Badge>
                </Flex>
                <VStack align="stretch" spacing={3} divider={<Divider />}>
                  {(summaryReport?.workload || []).length > 0 ? (
                    summaryReport.workload.map((item) => (
                      <Box key={item.assigneeId || item.assigneeName}>
                        <Text fontWeight="semibold" color={tableText}>
                          {item.assigneeName || "Unassigned"}
                        </Text>
                        <Text fontSize="sm" color={subText}>
                          {item.activeTickets} active · Urgent{" "}
                          {item.byPriority?.urgent || 0} · High{" "}
                          {item.byPriority?.high || 0}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text color={subText}>
                      No active assignee workload data available.
                    </Text>
                  )}
                </VStack>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="xl"
                p={5}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="bold" color={tableText}>
                    SLA Breaches
                  </Text>
                  <Badge colorScheme="red">{slaBreachedRows.length}</Badge>
                </Flex>
                <VStack align="stretch" spacing={3} divider={<Divider />}>
                  {slaBreachedRows.length > 0 ? (
                    slaBreachedRows.slice(0, 5).map((row, idx) => (
                      <Box key={`${row?.ticket?._id || idx}`}>
                        <Text fontWeight="semibold" color={tableText}>
                          {row?.ticket?.ticketId || "-"} ·{" "}
                          {row?.ticket?.title || "-"}
                        </Text>
                        <Text fontSize="sm" color={subText}>
                          {row?.priority || "-"} ·{" "}
                          {row?.response?.status || "-"} /{" "}
                          {row?.resolution?.status || "-"}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text color={subText}>No breached tickets.</Text>
                  )}
                </VStack>
              </Box>

              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="xl"
                p={5}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="bold" color={tableText}>
                    SLA At Risk
                  </Text>
                  <Badge colorScheme="orange">{slaAtRiskRows.length}</Badge>
                </Flex>
                <VStack align="stretch" spacing={3} divider={<Divider />}>
                  {slaAtRiskRows.length > 0 ? (
                    slaAtRiskRows.slice(0, 5).map((row, idx) => (
                      <Box key={`${row?.ticket?._id || idx}`}>
                        <Text fontWeight="semibold" color={tableText}>
                          {row?.ticket?.ticketId || "-"} ·{" "}
                          {row?.ticket?.title || "-"}
                        </Text>
                        <Text fontSize="sm" color={subText}>
                          {row?.priority || "-"} · Response{" "}
                          {Math.round(row?.response?.percentageUsed || 0)}% ·
                          Resolution{" "}
                          {Math.round(row?.resolution?.percentageUsed || 0)}%
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Text color={subText}>No at-risk tickets.</Text>
                  )}
                </VStack>
              </Box>
            </SimpleGrid>
          </>
        )}

        <Flex gap={4} flexWrap="wrap" mb={4} align="center">
          <TicketFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            priorityFilter={priorityFilter}
            setPriorityFilter={setPriorityFilter}
            severityFilter={severityFilter}
            setSeverityFilter={setSeverityFilter}
            assignedToFilter={assignedToFilter}
            setAssignedToFilter={setAssignedToFilter}
            requestedByFilter={requestedByFilter}
            setRequestedByFilter={setRequestedByFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            slaStatusFilter={slaStatusFilter}
            setSlaStatusFilter={setSlaStatusFilter}
            ageRangeFilter={ageRangeFilter}
            setAgeRangeFilter={setAgeRangeFilter}
            projectFilter={projectFilter}
            setProjectFilter={setProjectFilter}
            sprintFilter={sprintFilter}
            setSprintFilter={setSprintFilter}
            milestoneFilter={milestoneFilter}
            setMilestoneFilter={setMilestoneFilter}
            clientFilter={clientFilter}
            setClientFilter={setClientFilter}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onNewTicket={() => navigate("/Ticketing/create")}
            allTickets={allTickets}
            allEmployees={allEmployees}
            allClients={allClients}
            ticketViewMode={ticketViewMode}
            onToggleTicketViewMode={() => {
              setTicketViewMode((prev) =>
                prev === "personal" ? "all" : "personal",
              );
              setCurrentPage(1);
            }}
          />
        </Flex>

        <Tabs
          variant="enclosed"
          colorScheme="blue"
          index={activeTabIndex}
          onChange={(index) => {
            setActiveTabIndex(index);
            setCurrentPage(1);
          }}
        >
          <TabList flexWrap="wrap">
            {STATUS_TABS.map((tab) => (
              <Tab key={tab.label}>{tab.label}</Tab>
            ))}
          </TabList>

          <TabPanels>
            {STATUS_TABS.map((tab) => (
              <TabPanel key={tab.label} px={0}>
                <Box overflowX="auto">
                  <Table
                    variant="simple"
                    bg={cardBg}
                    rounded="md"
                    shadow="sm"
                    size="sm"
                    width="100%"
                  >
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th>Ticket Code</Th>
                        <Th>Title</Th>
                        <Th
                          cursor="pointer"
                          onClick={() => handleSort("status")}
                          _hover={{ bg: "gray.200" }}
                        >
                          Status {renderSortIcon("status")}
                        </Th>
                        <Th
                          cursor="pointer"
                          onClick={() => handleSort("priority")}
                          _hover={{ bg: "gray.200" }}
                        >
                          Priority {renderSortIcon("priority")}
                        </Th>
                        <Th>Severity</Th>
                        <Th
                          cursor="pointer"
                          onClick={() => handleSort("createdAt")}
                          _hover={{ bg: "gray.200" }}
                          title="Sort by creation date"
                        >
                          Age {renderSortIcon("createdAt")}
                        </Th>
                        <Th>SLA Due</Th>
                        <Th>Days Until Due</Th>
                        <Th>Category</Th>
                        <Th>Client</Th>
                        <Th>Requested By</Th>
                        <Th>Assigned To</Th>
                        <Th>Project</Th>
                        <Th>Sprint</Th>
                        <Th>Milestone</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {getStatusScopedTickets().map((ticket) => (
                        <Tr
                          key={ticket._id}
                          bg={ticket.isHighPriority ? "red.50" : undefined}
                          borderLeft={
                            ticket.isHighPriority ? "4px solid" : undefined
                          }
                          borderLeftColor={
                            ticket.isHighPriority ? "red.500" : undefined
                          }
                          _hover={{
                            bg: ticket.isHighPriority ? "red.100" : "gray.50",
                          }}
                        >
                          <Td color={tableText}>
                            <ChakraLink
                              color="blue.500"
                              fontWeight="500"
                              onClick={() =>
                                navigate(`/Ticketing/${ticket._id}`)
                              }
                              cursor="pointer"
                              _hover={{ textDecoration: "underline" }}
                            >
                              {ticket.ticketCode || "-"}
                            </ChakraLink>
                          </Td>
                          <Td color={tableText}>
                            <ChakraLink
                              color={tableText}
                              onClick={() =>
                                navigate(`/Ticketing/${ticket._id}`)
                              }
                              cursor="pointer"
                              _hover={{ textDecoration: "underline" }}
                            >
                              {ticket.title || "-"}
                            </ChakraLink>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={getWorkflowStateColor(
                                getTicketWorkflowState(ticket),
                              )}
                            >
                              {getWorkflowStateLabel(
                                getTicketWorkflowState(ticket),
                              )}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={getPriorityColor(ticket.priority)}
                            >
                              {ticket.priority || "-"}
                            </Badge>
                          </Td>
                          <Td color={subText}>{ticket.severity || "-"}</Td>
                          <Td color={subText} fontWeight="medium">
                            {getTicketAge(ticket)}
                          </Td>
                          <Td color={subText}>
                            {formatDate(getTicketDueDate(ticket))}
                          </Td>
                          <Td>
                            {(() => {
                              const dueInfo = getDaysUntilDue(ticket);
                              return dueInfo.status !== "none" ? (
                                <Badge
                                  colorScheme={getSlaStatusColor(
                                    dueInfo.status,
                                  )}
                                >
                                  {dueInfo.text}
                                </Badge>
                              ) : (
                                <Text color={subText}>-</Text>
                              );
                            })()}
                          </Td>
                          <Td color={subText}>{ticket.category || "-"}</Td>
                          <Td color={subText}>
                            {getDisplayName(ticket.client)}
                          </Td>
                          <Td color={subText}>
                            {getDisplayName(ticket.requestedBy)}
                          </Td>
                          <Td color={subText}>
                            {getDisplayName(ticket.assignedTo)}
                          </Td>
                          <Td color={subText}>
                            {ticket.projectId?.projectName || "-"}
                          </Td>
                          <Td color={subText}>
                            {ticket.sprintId?.sprintName || "-"}
                          </Td>
                          <Td color={subText}>
                            {ticket.milestoneId?.title ||
                              ticket.milestoneId?.status ||
                              "-"}
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              {/* <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                onClick={() => handleEdit(ticket)}
                                aria-label="Edit"
                              /> */}
                              <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                disabled
                                onClick={() => handleDelete(ticket._id)}
                                aria-label="Delete"
                                isLoading={deletingTicketId === ticket._id}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Flex justify="center" mt={4} gap={2} flexWrap="wrap">
                  {Array.from({ length: Math.max(1, totalTicketPages) }).map(
                    (_, index) => (
                      <Button
                        key={`${tab.label}-${index + 1}`}
                        size="sm"
                        colorScheme={
                          index + 1 === currentPage ? "blue" : "gray"
                        }
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Button>
                    ),
                  )}
                </Flex>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>

        {getStatusScopedTickets(activeTabStatus).length === 0 && (
          <Flex
            mt={12}
            direction="column"
            align="center"
            justify="center"
            p={{ base: 8, md: 16 }}
            bg={noTicketBg}
            rounded="2xl"
            border="2px solid"
            borderColor={cardBorder}
            textAlign="center"
            w="100%"
            maxW="500px"
            mx="auto"
            boxShadow="0 4px 20px rgba(0,0,0,0.05)"
          >
            <Box
              p={6}
              bg={cardBg}
              rounded="full"
              mb={8}
              boxShadow="sm"
              border="1px solid"
              borderColor={cardBorder}
            >
              <Image
                src="/flow.png"
                alt="No tickets"
                boxSize={{ base: "120px", md: "160px" }}
                opacity={0.9}
              />
            </Box>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="900"
              color={tableText}
              mb={3}
              letterSpacing="-0.5px"
            >
              All caught up!
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={subText}
              mb={2}
              lineHeight="1.6"
            >
              No tickets match your current filters.
            </Text>
            <Text fontSize="sm" color={subText} mb={8} opacity={0.8}>
              Try adjusting your filters or create a new one to get started.
            </Text>

            <VStack spacing={3} w="100%" mb={6}>
              <Button
                w="100%"
                leftIcon={<FiPlus />}
                colorScheme="blue"
                size="lg"
                onClick={() => navigate("/Ticketing/create")}
                fontWeight="600"
                boxShadow="md"
                _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                transition="all 0.2s"
              >
                Create New Ticket
              </Button>
              <Button
                w="100%"
                leftIcon={<FiRefreshCw />}
                variant="outline"
                colorScheme="blue"
                size="lg"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter(null);
                  setPriorityFilter(null);
                  setSeverityFilter(null);
                  setAssignedToFilter(null);
                  setRequestedByFilter(null);
                  setCategoryFilter(null);
                  setSlaStatusFilter(null);
                  setAgeRangeFilter(null);
                  setProjectFilter(null);
                  setSprintFilter(null);
                  setMilestoneFilter(null);
                  setClientFilter(null);
                }}
                fontWeight="600"
              >
                Clear All Filters
              </Button>
            </VStack>

            <Divider my={2} />

            <Text fontSize="xs" color={subText} mt={6} fontWeight="500">
              💡 Tip: Use filters to quickly find tickets by status, priority,
              or assignee
            </Text>
          </Flex>
        )}

        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent
            bg={cardBg}
            maxW="4xl"
            borderRadius="2xl"
            border="1px solid"
            borderColor={cardBorder}
          >
            <ModalHeader color={tableText}>Ticket Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedTicket && (
                <Tabs w="100%" variant="soft-rounded" colorScheme="blue" isLazy>
                  <TabList mb={4} flexWrap="wrap" gap={2}>
                    <Tab>Edit</Tab>
                    <Tab>Comments</Tab>
                    <Tab>SLA</Tab>
                    <Tab>History</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <VStack align="start" spacing={4} w="100%">
                        <Text color={tableText}>
                          <b>Ticket Code:</b> {selectedTicket.ticketCode}
                        </Text>
                        <Input
                          placeholder="Title"
                          value={editForm.title}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />
                        <Input
                          placeholder="Description"
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        />
                        <Select
                          value={editForm.workflowState}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              workflowState: e.target.value,
                            }))
                          }
                        >
                          {allowedStatuses.map((state) => (
                            <option key={state} value={state}>
                              {getWorkflowStateLabel(state)}
                            </option>
                          ))}
                        </Select>
                        {workflowMetaLoading && (
                          <Text fontSize="sm" color={subText}>
                            Loading workflow transitions...
                          </Text>
                        )}
                        {editForm.workflowState !==
                          getTicketWorkflowState(selectedTicket) && (
                          <Alert status="info" borderRadius="md" fontSize="sm">
                            <AlertIcon />
                            Status updates are submitted through the workflow
                            transition API.
                          </Alert>
                        )}
                        <Select
                          value={editForm.priority}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              priority: e.target.value,
                            }))
                          }
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Urgent">Urgent</option>
                        </Select>
                        <Input
                          type="datetime-local"
                          placeholder="Due Date"
                          value={editForm.dueDate}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              dueDate: e.target.value,
                            }))
                          }
                        />

                        {/* Attachments Section */}
                        <Box
                          border="1px solid"
                          borderColor={cardBorder}
                          borderRadius="md"
                          p={4}
                        >
                          <Text fontWeight="semibold" mb={2} color={tableText}>
                            Attachments
                          </Text>

                          {/* Existing Attachments */}
                          {selectedTicket?.attachments &&
                          selectedTicket.attachments.length > 0 ? (
                            <VStack align="stretch" spacing={2} mb={3}>
                              {selectedTicket.attachments.map(
                                (attachment, index) => (
                                  <Flex
                                    key={`${attachment}-${index}`}
                                    align="center"
                                    justify="space-between"
                                    bg={noTicketBg}
                                    p={2}
                                    borderRadius="md"
                                  >
                                    <HStack spacing={2} flex={1}>
                                      <Badge colorScheme="blue">
                                        {attachment.split(".").pop()}
                                      </Badge>
                                      <ChakraLink
                                        href={attachment}
                                        isExternal
                                        color="blue.500"
                                        fontSize="sm"
                                        isTruncated
                                        maxW="300px"
                                      >
                                        {attachment.split("/").pop()}
                                      </ChakraLink>
                                    </HStack>
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() =>
                                        handleDeleteAttachment(attachment)
                                      }
                                      aria-label="Delete attachment"
                                    />
                                  </Flex>
                                ),
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color={subText} mb={3}>
                              No attachments yet
                            </Text>
                          )}

                          {/* File Upload */}
                          <HStack spacing={2}>
                            <Input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              accept=".jpeg,.jpg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                              size="sm"
                              display="none"
                            />
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                              isLoading={uploadingAttachment}
                              leftIcon={<FiPlus />}
                            >
                              Upload File
                            </Button>
                            <Text fontSize="xs" color={subText}>
                              Max 10MB • PDF, Images, Documents, Archives
                            </Text>
                          </HStack>
                        </Box>

                        <Input
                          placeholder="Category"
                          value={editForm.category}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              category: e.target.value,
                            }))
                          }
                        />
                        <Select
                          placeholder="Assign to"
                          value={editForm.assignedTo}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              assignedTo: e.target.value,
                            }))
                          }
                        >
                          {allEmployees.map((employee) => (
                            <option key={employee._id} value={employee._id}>
                              {getDisplayName(employee)}
                            </option>
                          ))}
                        </Select>
                        <Textarea
                          placeholder="Transition comment (optional, used when status changes)"
                          value={transitionComment}
                          onChange={(e) => setTransitionComment(e.target.value)}
                        />
                      </VStack>
                    </TabPanel>

                    <TabPanel px={0}>
                      <VStack align="stretch" spacing={4}>
                        <HStack align="start" spacing={3}>
                          <Select
                            w="180px"
                            value={newCommentType}
                            onChange={(e) => setNewCommentType(e.target.value)}
                          >
                            <option value="public">Public</option>
                            <option value="internal">Internal</option>
                          </Select>
                          <Textarea
                            placeholder="Add a comment"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                          />
                          <Button
                            colorScheme="blue"
                            onClick={handleCreateComment}
                            isLoading={submittingComment}
                          >
                            Add
                          </Button>
                        </HStack>

                        {commentsLoading ? (
                          <Spinner />
                        ) : comments.length > 0 ? (
                          <VStack align="stretch" spacing={3}>
                            {comments.map((comment) => (
                              <Box
                                key={comment._id}
                                p={3}
                                border="1px solid"
                                borderColor={cardBorder}
                                borderRadius="md"
                              >
                                {editingCommentId === comment._id ? (
                                  <VStack align="stretch" spacing={2}>
                                    <Textarea
                                      value={editCommentContent}
                                      onChange={(e) =>
                                        setEditCommentContent(e.target.value)
                                      }
                                    />
                                    <HStack>
                                      <Button
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={handleSaveEditComment}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleCancelEditComment}
                                      >
                                        Cancel
                                      </Button>
                                    </HStack>
                                  </VStack>
                                ) : (
                                  <Flex
                                    justify="space-between"
                                    align="start"
                                    gap={3}
                                  >
                                    <Box>
                                      <Text fontWeight="bold" color={tableText}>
                                        {getDisplayName(comment.createdBy)}
                                      </Text>
                                      <Text fontSize="sm" color={subText}>
                                        {formatDate(comment.createdAt)} ·{" "}
                                        {comment.commentType}
                                      </Text>
                                      <Text mt={2} color={tableText}>
                                        {comment.content}
                                      </Text>
                                    </Box>
                                    <HStack>
                                      {/* <Button
                                        size="xs"
                                        onClick={() =>
                                          handleEditComment(comment)
                                        }
                                      >
                                        Edit
                                      </Button> */}
                                      <Button
                                        size="xs"
                                        colorScheme="red"
                                        variant="outline"
                                        onClick={() =>
                                          handleDeleteComment(comment._id)
                                        }
                                      >
                                        Delete
                                      </Button>
                                    </HStack>
                                  </Flex>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        ) : (
                          <Text color={subText}>No comments yet.</Text>
                        )}
                      </VStack>
                    </TabPanel>

                    <TabPanel px={0}>
                      {ticketSlaLoading ? (
                        <Spinner />
                      ) : ticketSla ? (
                        <VStack align="stretch" spacing={3}>
                          <Text color={tableText}>
                            <b>Priority:</b> {ticketSla.priority || "-"}
                          </Text>
                          <Text color={tableText}>
                            <b>Breached:</b>{" "}
                            {ticketSla.isBreached ? "Yes" : "No"}
                          </Text>
                          <Text color={tableText}>
                            <b>Paused:</b> {ticketSla.isPaused ? "Yes" : "No"}
                          </Text>
                          <Divider />
                          <Text color={tableText} fontWeight="bold">
                            Response SLA
                          </Text>
                          <Text color={subText}>
                            Status: {ticketSla.response?.status || "-"} · Used{" "}
                            {Math.round(
                              ticketSla.response?.percentageUsed || 0,
                            )}
                            % · Remaining {ticketSla.response?.remaining ?? "-"}{" "}
                            min
                          </Text>
                          <Text color={tableText} fontWeight="bold">
                            Resolution SLA
                          </Text>
                          <Text color={subText}>
                            Status: {ticketSla.resolution?.status || "-"} · Used{" "}
                            {Math.round(
                              ticketSla.resolution?.percentageUsed || 0,
                            )}
                            % · Remaining{" "}
                            {ticketSla.resolution?.remaining ?? "-"} min
                          </Text>
                          <HStack pt={2} spacing={3} flexWrap="wrap">
                            <Button
                              size="sm"
                              leftIcon={<FiPauseCircle />}
                              onClick={() => handleSlaAction("pause")}
                              isLoading={slaActionLoading === "pause"}
                              colorScheme="orange"
                            >
                              Pause SLA
                            </Button>
                            <Button
                              size="sm"
                              leftIcon={<FiPlayCircle />}
                              onClick={() => handleSlaAction("resume")}
                              isLoading={slaActionLoading === "resume"}
                              colorScheme="green"
                            >
                              Resume SLA
                            </Button>
                            <Button
                              size="sm"
                              leftIcon={<FiActivity />}
                              onClick={() => handleSlaAction("calculate")}
                              isLoading={slaActionLoading === "calculate"}
                              variant="outline"
                            >
                              Recalculate
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <Text color={subText}>
                          SLA details unavailable for this ticket or your role.
                        </Text>
                      )}
                    </TabPanel>

                    <TabPanel px={0}>
                      {historyLoading ? (
                        <Spinner />
                      ) : historyRows.length > 0 ? (
                        <VStack align="stretch" spacing={3}>
                          {historyRows.map((item) => (
                            <Box
                              key={item._id}
                              p={3}
                              border="1px solid"
                              borderColor={cardBorder}
                              borderRadius="md"
                            >
                              <Text fontWeight="bold" color={tableText}>
                                {item.changeType}
                              </Text>
                              <Text fontSize="sm" color={subText}>
                                {formatDate(item.createdAt)} · by{" "}
                                {getDisplayName(item.changedBy)}
                              </Text>
                              {Array.isArray(item.changes) &&
                                item.changes.length > 0 && (
                                  <VStack align="start" spacing={1} mt={2}>
                                    {item.changes.map((change, index) => (
                                      <Text
                                        key={`${item._id}-${index}`}
                                        fontSize="sm"
                                        color={subText}
                                      >
                                        {change.field}:{" "}
                                        {String(change.oldValue ?? "-")} →{" "}
                                        {String(change.newValue ?? "-")}
                                      </Text>
                                    ))}
                                  </VStack>
                                )}
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text color={subText}>No history entries yet.</Text>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={handleSave}
                colorScheme="blue"
                isLoading={saveLoading}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
      {/* Ticketing User Manual Modal */}
      <Modal isOpen={isInfoOpen} onClose={onInfoClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ticketing — User Manual</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Overview</Text>
              <Text fontSize="sm" color={subText}>
                This guide explains Ticketing concepts: ticket lifecycle,
                workflow transitions, SLA behavior, assignment rules, and how to
                interact with comments and notifications.
              </Text>

              <Divider />

              <Text fontWeight="semibold">Common workflows</Text>
              <Text fontSize="sm" color={subText}>
                - Create a ticket to report an issue or request.\n- Assign to an
                employee to start work.\n- Use workflow transitions to move
                tickets through stages (Open → In Progress → Resolved → Closed).
              </Text>

              <Divider />

              <Text fontWeight="semibold">Tips</Text>
              <Text fontSize="sm" color={subText}>
                Use comments for context, add transition comments for audit
                trail, and monitor SLA indicators for timely action.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onInfoClose} size="sm">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
